// Dok 17 – Bas-P inkorg: samlar allt som väntar på Bas-P:s beslut.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Inbox, CheckCircle2, ArrowRightLeft, MessagesSquare, ClipboardCheck, DraftingCompass, RefreshCw, AlertTriangle } from "lucide-react";
import { transferSubmissionRisks, splitRiskLines } from "@/lib/basProcess/transferRisks";

interface Props {
  projectId: string;
  onOpenTab?: (tab: string) => void;
}

interface Submission {
  id: string;
  project_id: string;
  discipline: string;
  company: string | null;
  status: string;
  residual_risks_construction: string | null;
  residual_risks_use: string | null;
  submitted_at: string | null;
}

interface QaEntry {
  id: string;
  entry_number: number;
  subject: string;
  responsible_function: string | null;
  status: string;
}

interface IncidentFlag {
  id: string;
  description: string;
  amp_revision_note: string | null;
  occurred_at: string;
}

interface Prep {
  id: string;
  name: string;
  review_status: string;
  submitted_at: string | null;
}

const BaspInbox: React.FC<Props> = ({ projectId, onOpenTab }) => {
  const [subs, setSubs] = useState<Submission[]>([]);
  const [qa, setQa] = useState<QaEntry[]>([]);
  const [preps, setPreps] = useState<Prep[]>([]);
  const [ampFlags, setAmpFlags] = useState<IncidentFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, q, p, inc] = await Promise.all([
      supabase
        .from("designer_submissions")
        .select("id, project_id, discipline, company, status, residual_risks_construction, residual_risks_use, submitted_at")
        .eq("project_id", projectId)
        .in("status", ["submitted", "accepted"])
        .order("submitted_at", { ascending: true }),
      supabase
        .from("qa_log_entries")
        .select("id, entry_number, subject, responsible_function, status")
        .eq("project_id", projectId)
        .eq("status", "open")
        .order("entry_number", { ascending: true }),
      supabase
        .from("work_preparations")
        .select("id, name, review_status, submitted_at")
        .eq("project_id", projectId)
        .in("review_status", ["submitted", "pending", "in_review"])
        .order("submitted_at", { ascending: true }),
      supabase
        .from("incident_reports")
        .select("id, description, amp_revision_note, occurred_at, status")
        .eq("project_id", projectId)
        .eq("requires_amp_revision", true)
        .neq("status", "closed")
        .order("occurred_at", { ascending: false }),
    ]);
    if (s.error || q.error || p.error) toast.error("Kunde inte hämta inkorgen");
    setSubs((s.data || []) as Submission[]);
    setQa((q.data || []) as QaEntry[]);
    setPreps((p.data || []) as Prep[]);
    setAmpFlags((inc.data || []) as IncidentFlag[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingSubs = useMemo(() => subs.filter((s) => s.status === "submitted"), [subs]);
  const acceptedSubs = useMemo(() => subs.filter((s) => s.status === "accepted"), [subs]);
  const total = pendingSubs.length + qa.length + preps.length + ampFlags.length;

  const accept = async (s: Submission) => {
    setBusy(s.id);
    const { error } = await supabase
      .from("designer_submissions")
      .update({ status: "accepted", reviewed_at: new Date().toISOString() })
      .eq("id", s.id);
    if (error) toast.error("Kunde inte godkänna inlämningen");
    else toast.success(`Inlämning från ${s.discipline} godkänd`);
    setBusy(null);
    load();
  };

  const transfer = async (s: Submission) => {
    setBusy(s.id);
    try {
      const res = await transferSubmissionRisks(s);
      if (res.construction === 0 && res.use === 0) {
        toast.info("Inga nya risker att överföra – allt är redan infört.");
      } else {
        toast.success(
          `Överfört: ${res.construction} risk(er) till byggskedet och ${res.use} till bruksskedet.`,
        );
      }
    } catch {
      toast.error("Överföringen misslyckades");
    }
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5" /> Bas-P inkorg
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Allt som väntar på ditt beslut: projektörsinlämningar, obesvarade frågor och
              arbetsberedningar under granskning.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={total > 0 ? "destructive" : "secondary"}>{total} att hantera</Badge>
            <Button variant="ghost" size="icon" onClick={load} aria-label="Uppdatera">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {ampFlags.length > 0 && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" /> Tillbud som kräver
              AMP-revidering
              <Badge variant="destructive">{ampFlags.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {ampFlags.map((i) => (
              <div
                key={i.id}
                className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {new Date(i.occurred_at).toLocaleDateString("sv-SE")} – {i.description.slice(0, 90)}
                  </p>
                  {i.amp_revision_note && (
                    <p className="text-sm text-muted-foreground">{i.amp_revision_note}</p>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => onOpenTab?.("tillbud")}>
                  Öppna tillbudet
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <DraftingCompass className="h-4 w-4" /> Projektörsinlämningar att granska
            <Badge variant="outline">{pendingSubs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
          {!loading && pendingSubs.length === 0 && (
            <p className="text-sm text-muted-foreground">Inget väntar på granskning.</p>
          )}
          {pendingSubs.map((s) => (
            <div
              key={s.id}
              className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {s.discipline}
                  {s.company ? ` – ${s.company}` : ""}
                </p>
                <p className="text-sm text-muted-foreground">
                  {splitRiskLines(s.residual_risks_construction).length} byggrisker ·{" "}
                  {splitRiskLines(s.residual_risks_use).length} bruksrisker
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onOpenTab?.("projektorinlamning")}>
                  Öppna
                </Button>
                <Button size="sm" disabled={busy === s.id} onClick={() => accept(s)}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Godkänn
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Godkända inlämningar – för in i risksammanställningen
            <Badge variant="outline">{acceptedSubs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!loading && acceptedSubs.length === 0 && (
            <p className="text-sm text-muted-foreground">Inga godkända inlämningar ännu.</p>
          )}
          {acceptedSubs.map((s) => (
            <div
              key={s.id}
              className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="font-medium">{s.discipline}</p>
                <p className="text-sm text-muted-foreground">
                  Byggskede → uppföljning utförande, bruksskede → kvarvarande bruksrisker.
                </p>
              </div>
              <Button variant="secondary" size="sm" disabled={busy === s.id} onClick={() => transfer(s)}>
                <ArrowRightLeft className="h-4 w-4 mr-1" /> Överför restrisker
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessagesSquare className="h-4 w-4" /> Öppna frågor (Dok 25)
              <Badge variant="outline">{qa.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {!loading && qa.length === 0 && (
              <p className="text-sm text-muted-foreground">Inga obesvarade frågor.</p>
            )}
            {qa.map((e) => (
              <div key={e.id} className="rounded-md border p-2 text-sm">
                <span className="font-medium">#{e.entry_number}</span> {e.subject}
                {e.responsible_function && (
                  <span className="text-muted-foreground"> · {e.responsible_function}</span>
                )}
              </div>
            ))}
            {qa.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => onOpenTab?.("qalogg")}>
                Öppna Q&A-loggen
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" /> Arbetsberedningar i granskning
              <Badge variant="outline">{preps.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {!loading && preps.length === 0 && (
              <p className="text-sm text-muted-foreground">Inget väntar på granskning.</p>
            )}
            {preps.map((p) => (
              <div key={p.id} className="rounded-md border p-2 text-sm">
                {p.name}
                <span className="text-muted-foreground"> · {p.review_status}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BaspInbox;
